/**
 * Every way a save can refuse, and the two sentences a player reads when it does.
 *
 * "Import failed" is the message this file exists to prevent. A save is a hundred hours, and a
 * player told only that something went wrong cannot tell a full disk from a file from next month's
 * build from a typo in the filename, though every one of those has a different thing to do next.
 *
 * The message says what happened, the advice says what would help, and they are separate for the
 * same reason `sessionErrors.ts` keeps them apart: the failures anyone actually meets differ mostly
 * in the second half.
 *
 * One exhaustive switch per union, so a reason added without wording is a type error rather than a
 * screen saying nothing. That includes each section's own failures, because `sectionRejected`
 * carries one whole and flattening it here would throw away the item id that makes it useful.
 */

import type { InventoryFailure } from '@/sim/inventory/inventoryResult';
import type { ProgressionFailure } from '@/sim/progression/progressionResult';
import type { SaveFailure } from '@/save/saveResult';

export interface SaveFailureText {
  readonly message: string;
  /** Empty when there is nothing honest to suggest. */
  readonly advice: string;
}

export function saveFailureText(failure: SaveFailure): SaveFailureText {
  switch (failure.reason) {
    case 'malformed':
      return {
        message: `This save is missing or misshapen at ${failure.field}.`,
        advice: 'Nothing was changed. Try an export of the same character from another browser.',
      };
    case 'fromNewerBuild':
      return {
        message:
          `This save was written by a newer build: version ${num(failure.found)}. ` +
          `This one reads ${num(failure.current)}.`,
        advice: 'Update the game and try again. Nothing was overwritten.',
      };
    case 'noMigrationPath':
      return {
        message:
          `This save is version ${num(failure.found)}, and nothing here knows how to move ` +
          `version ${num(failure.missingFrom)} forward.`,
        advice: 'The character cannot be recovered here. Keep the file, it is not damaged.',
      };
    case 'sectionRejected':
      return {
        message:
          `The ${failure.section} in this save could not be read: ` + `${sectionReason(failure)}.`,
        advice: 'This usually means the save is from a build with different content in it.',
      };
    case 'quotaExceeded':
      return {
        message: 'This browser will not store any more. The save did not land.',
        advice: 'Free up site data, or export this character and play in another browser.',
      };
    case 'storageUnavailable':
      return {
        message: `This browser is not storing anything: ${failure.detail}.`,
        advice: 'Private browsing usually causes this. Nothing here survives a reload.',
      };
    case 'storageFailed':
      return {
        message: `Storage refused the ${failure.operation}: ${failure.detail}.`,
        advice: 'Your previous save is untouched. Try again.',
      };
    case 'noSuchProfile':
      return {
        message: 'There is no character stored under that id.',
        advice: '',
      };
    case 'notJson':
      return {
        message: `That file is not a save: ${failure.detail}.`,
        advice: 'Pick the .tbd-save.json file the export button produced.',
      };
    case 'notASaveFile':
      return {
        message: `That file is JSON, but it is not one of ours: it is marked ${failure.found}.`,
        advice: 'Pick the .tbd-save.json file the export button produced.',
      };
    case 'fromNewerFormat':
      return {
        message:
          `That file uses save format ${num(failure.found)}, ` +
          `and this build reads ${num(failure.current)}.`,
        advice: 'Update the game and import it again. Nothing was overwritten.',
      };
    case 'fileTooLarge':
      return {
        message:
          `That file is ${megabytes(failure.bytes)}, ` +
          `and no save is bigger than ${megabytes(failure.limit)}.`,
        advice: 'It was not read. Check that you picked the save file.',
      };
  }
}

/** The rejected section's own failure, dispatched to whichever vocabulary raised it. */
function sectionReason(failure: Extract<SaveFailure, { reason: 'sectionRejected' }>): string {
  switch (failure.section) {
    case 'inventory':
      return inventoryReason(failure.failure);
    case 'progression':
      return progressionReason(failure.failure);
  }
}

/**
 * Progression's failure, in the vocabulary it was raised in.
 *
 * The overspend cases read as a save that has been edited rather than as an error, because that is
 * almost always what they are, and nothing here is signed so it is an expected thing to meet.
 */
function progressionReason(failure: ProgressionFailure): string {
  switch (failure.reason) {
    case 'unknownPerk':
      return `it has taken "${failure.perkId}", which is not a perk in this build`;
    case 'invalidRank':
      return `"${failure.perkId}" is at rank ${num(failure.rank)}`;
    case 'atMaxRank':
      return `"${failure.perkId}" is past its ${num(failure.maxRanks)} ranks`;
    case 'atStatCap':
      return `${failure.statId} is past its cap of ${num(failure.cap)}`;
    case 'levelTooLow':
      return `"${failure.perkId}" needs level ${num(failure.minLevel)}`;
    case 'missingRequirement':
      return `"${failure.perkId}" is taken without "${failure.requires}"`;
    case 'noPerkPoints':
      return 'a perk is taken with no perk points left';
    case 'noStatPoints':
      return 'a stat is raised with no stat points left';
  }
}

/**
 * The inventory's failure, in the vocabulary it was raised in.
 *
 * Short phrases rather than sentences, because each one is read out inside the sentence above.
 */
function inventoryReason(failure: InventoryFailure): string {
  switch (failure.reason) {
    case 'unknownItem':
      return `it carries "${failure.defId}", which is not an item in this build`;
    case 'unknownSlot':
      return `it equips something to "${failure.slot}", which is not a slot in this build`;
    case 'unknownAffix':
      return `a weapon rolled "${failure.affixId}", which is not an affix in this build`;
    case 'unknownRarity':
      return `a weapon is "${failure.rarity}", which is not a rarity in this build`;
    case 'affixSlotMismatch':
      return `"${failure.affixId}" is used in the wrong half of a roll`;
    case 'rollOnNonWeapon':
      return `"${failure.defId}" carries a weapon roll and is not a weapon`;
    case 'invalidCount':
      return `a stack holds ${num(failure.count)} of something`;
    case 'overCapacity':
      return (
        `the ${failure.container} holds ${failure.weightKg.toFixed(1)}kg ` +
        `and takes ${failure.capacityKg.toFixed(1)}kg`
      );
    case 'noSuchStack':
      return `the ${failure.container} has no stack at ${num(failure.index)}`;
    case 'notStackable':
      return `"${failure.defId}" is stacked and does not stack`;
    case 'stackMismatch':
      return `"${failure.from}" is stacked with "${failure.to}"`;
    case 'sameStack':
      return `a stack is moved onto itself at ${num(failure.index)}`;
    case 'sameContainer':
      return `the ${failure.container} is moved into itself`;
    case 'wrongSlot':
      return `"${failure.defId}" is equipped to the ${failure.slot} slot`;
    case 'notEquippable':
      return `"${failure.defId}" is equipped and cannot be`;
    case 'emptySlot':
      return `the ${failure.slot} slot is empty where something was expected`;
    case 'versionMismatch':
      return (
        `the inventory is version ${num(failure.found)} ` +
        `and this build reads ${num(failure.expected)}`
      );
  }
}

/** A number in a sentence, spelled out once so the interpolations stay inside the line limit. */
function num(value: number): string {
  return String(value);
}

function megabytes(bytes: number): string {
  return `${(bytes / 1_000_000).toFixed(1)}MB`;
}
